import {
  Transaction,
  TransactionType,
  TransactionStatus,
  TransactionBlockchainInfoInput,
  NationalIdentification,
  OwnershipProofInput,
} from "~/endpoints/transactions/types";
import { TransactionAction } from "~/templates/transactions/types";
import { VASP } from "~/types/vaspid";

const ENCRYPTED_TEXT = "Encrypted" as const;

const currencyFormat = new Intl.NumberFormat("en-US", {
  useGrouping: true,
  minimumFractionDigits: 4,
});

type Maybe<T> = T | null | undefined;
export type Direction = "outgoing" | "incoming" | "internal";

export class TransactionWrap {
  public originatorVASP: VASP | null | undefined;
  public beneficiaryVASP: VASP | null | undefined;
  private originalTransaction: Transaction;

  constructor(originalTransaction: Transaction) {
    this.originalTransaction = originalTransaction;
  }

  get beneficiaryVASPdid() {
    return this.originalTransaction.beneficiaryVASPdid;
  }
  get originatorVASPdid() {
    return this.originalTransaction.originatorVASPdid;
  }

  /**
   * Returns triplet [VASP?, VASPDid?, direction?]
   * If VASPDid then VASP is present.
   */
  direction(me?: Pick<VASP, "did"> | null) {
    let counterVASPDid: string | undefined | null = null;
    let counterVASP: VASP | undefined | null = null;
    let direction: Direction | undefined = undefined;

    if (me && me.did === this.originatorVASPdid && me.did === this.beneficiaryVASPdid) {
      direction = "internal";
      counterVASP = this.originatorVASP;
      counterVASPDid = this.originatorVASPdid;
    } else if (me && me.did === this.originatorVASPdid) {
      direction = "outgoing";
      counterVASP = this.beneficiaryVASP;
      counterVASPDid = this.beneficiaryVASPdid;
    } else if (me && me.did === this.beneficiaryVASPdid) {
      direction = "incoming";
      counterVASP = this.originatorVASP;
      counterVASPDid = this.originatorVASPdid;
    } else {
      // Non customer
      direction = "incoming";
      counterVASP = this.originatorVASP;
      counterVASPDid = this.originatorVASPdid;
    }
    return [counterVASP, counterVASPDid, direction] as const;
  }

  get id() {
    return this.originalTransaction.id;
  }
  get isEncrypted() {
    return Boolean(this.originalTransaction.ivms101Encrypted);
  }
  get transactionAsset() {
    return this.originalTransaction.transactionAsset;
  }
  get transactionAmount(): string {
    return this.originalTransaction.transactionAmount;
  }
  get transactionAssetDecimals(): number {
    return this.originalTransaction.transactionAssetDecimals;
  }

  get amountInLocalCurrency(): { amountInLocalCurrency: number; currency: string } | undefined {
    return this.originalTransaction.amountInLocalCurrency;
  }
  get beneficiaryAmountInLocalCurrency(): { amountInLocalCurrency: number; currency: string } | undefined {
    return this.originalTransaction.beneficiaryAmountInLocalCurrency;
  }
  get transactionAmountWithDecimals(): string {
    const human = Number(this.transactionAmount) / Math.pow(10, this.originalTransaction.transactionAssetDecimals);
    return currencyFormat.format(human);
  }

  get businessDaysSinceRequestForInfo(): number | undefined {
    return this.originalTransaction.businessDaysSinceRequestForInfo;
  }

  get type(): TransactionType {
    return this.originalTransaction.transactionType;
  }
  get typeIsTravelRule(): boolean {
    return this.type === TransactionType.TRAVELRULE;
  }

  get typeIsUnknown(): boolean {
    return this.type === TransactionType.UNKNOWN;
  }

  get typeIsBelowThreshold(): boolean {
    return this.type === TransactionType.BELOW_THRESHOLD;
  }
  /** AKA Un-hosted */
  get typeIsNonCustodial(): boolean {
    return this.type === TransactionType.NON_CUSTODIAL;
  }

  get beneficiaryDid(): string | null {
    return this.originalTransaction.beneficiaryDid;
  }
  get originatorDid(): string | null {
    return this.originalTransaction.originatorDid;
  }

  get beneficiaryRef(): string | null {
    return this.originalTransaction.beneficiaryRef;
  }
  get originatorRef(): string | null {
    return this.originalTransaction.originatorRef;
  }

  get beneficiaryProof(): Maybe<OwnershipProofInput> {
    return this.originalTransaction.beneficiaryProof;
  }

  get originatorProof(): Maybe<OwnershipProofInput> {
    return this.originalTransaction.originatorProof;
  }

  /** For transaction list boxed value display */
  get originatorDisplay(): string {
    return this.originatorRef || (this.originatorName && "Identified") || "Unknown";
  }
  /** For transaction list boxed value display */
  get beneficiaryDisplay(): string {
    return this.beneficiaryRef || (this.beneficiaryName && "Identified") || "Unknown";
  }

  get actions(): TransactionAction[] | null {
    return (this.originalTransaction.actions as unknown) as TransactionAction[] | null;
  }

  get lastActivity() {
    return new Date(this.originalTransaction.updatedAt);
  }

  get createdAt() {
    return new Date(this.originalTransaction.createdAt);
  }
  get updatedAt() {
    return new Date(this.originalTransaction.updatedAt);
  }

  get blockchainTransactionTimestamp() {
    if (this.originalTransaction.blockchainTransactionTimestamp) {
      return new Date(this.originalTransaction.blockchainTransactionTimestamp);
    }
    return null;
  }

  get status() {
    return this.originalTransaction.status;
  }
  /**
   * Terminal state (`CANCELLED`)
   */
  get isCancelled() {
    return this.status === TransactionStatus.CANCELLED;
  }
  /**
   * Terminal state (`REJECTED`)
   */
  get isRejected() {
    return this.status === TransactionStatus.REJECTED;
  }
  /**
   * Terminal state (`DECLINED`)
   */
  get isDeclined() {
    return this.status === TransactionStatus.DECLINED;
  }
  get isNew() {
    return this.status === TransactionStatus.NEW;
  }

  get isSent() {
    return this.status === TransactionStatus.SENT;
  }
  get isWaiting() {
    return this.status === TransactionStatus.WAITING_FOR_INFORMATION;
  }

  get isWaitingForTR() {
    return this.status === TransactionStatus.WAITING_FOR_TR;
  }

  get isIncomplete() {
    return this.status === TransactionStatus.INCOMPLETE;
  }

  get isAccepted() {
    return this.status === TransactionStatus.ACCEPTED;
  }

  get isMissingBeneficiaryData() {
    return this.status === TransactionStatus.MISSING_BENEFICIARY_DATA;
  }

  get isNotReady() {
    return this.status === TransactionStatus.NOT_READY;
  }

  /**
   * Terminal state
   */
  get isSaved() {
    return this.status === TransactionStatus.SAVED;
  }
  get isAcknowledged() {
    return this.status === TransactionStatus.ACK;
  }
  get ivms101() {
    return this.originalTransaction.ivms101;
  }
  get beneficiary() {
    return this.ivms101?.beneficiary;
  }
  get originator() {
    return this.ivms101?.originator;
  }
  get beneficiaryNatural() {
    const person = this.beneficiary?.beneficiaryPersons?.[0];
    return person?.naturalPerson;
  }
  get beneficiaryLegal() {
    const person = this.beneficiary?.beneficiaryPersons?.[0];
    return person?.legalPerson;
  }
  get originatorNatural() {
    const person = this.originator?.originatorPersons?.[0];
    return person?.naturalPerson;
  }
  get originatorLegal() {
    const person = this.originator?.originatorPersons?.[0];
    return person?.legalPerson;
  }

  get transactionBlockchainInfo(): Maybe<TransactionBlockchainInfoInput> {
    return this.originalTransaction.transactionBlockchainInfo;
  }
  get originatorBlockchainAddress(): Maybe<string> {
    return this.originalTransaction.transactionBlockchainInfo?.origin;
  }
  get transactionHash(): Maybe<string> {
    return this.originalTransaction.transactionBlockchainInfo?.txHash;
  }
  get beneficiaryBlockchainAddress(): Maybe<string> {
    return this.transactionBlockchainInfo?.destination;
  }

  get beneficiaryFirstName(): Maybe<string> {
    if (this.beneficiaryNatural) {
      const name = this.beneficiaryNatural.name?.[0]?.nameIdentifier?.[0];
      return name ? name.secondaryIdentifier : undefined;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }

  get beneficiaryLastName(): Maybe<string> {
    if (this.beneficiaryLegal) {
      return this.beneficiaryLegal.name?.nameIdentifier?.[0]?.legalPersonName;
    } else if (this.beneficiaryNatural) {
      const name = this.beneficiaryNatural.name?.[0]?.nameIdentifier?.[0];
      return name ? name.primaryIdentifier : undefined;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }

  get beneficiaryName(): Maybe<string> {
    if (this.beneficiaryLegal) {
      return this.beneficiaryLegal.name?.nameIdentifier?.[0]?.legalPersonName;
    } else if (this.beneficiaryNatural) {
      const name = this.beneficiaryNatural.name?.[0]?.nameIdentifier?.[0];
      return name ? [name.secondaryIdentifier, name.primaryIdentifier].filter(Boolean).join(" ") : undefined;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }

  get beneficiaryAddress(): any {
    if (this.beneficiaryLegal) {
      //To make Typescript happy, this line of code was needed
      return ((this.beneficiaryLegal.geographicAddress as unknown) as unknown[])?.[0];
    } else if (this.beneficiaryNatural) {
      return this.beneficiaryNatural.geographicAddress?.[0];
    } else {
      return null;
    }
  }
  get beneficiaryAddressLine1(): Maybe<string> {
    if (this.beneficiaryAddress) {
      return this.beneficiaryAddress.addressLine?.[0];
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get beneficiaryAddressLine2(): Maybe<string> {
    if (this.beneficiaryAddress) {
      return this.beneficiaryAddress.addressLine?.[1];
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get beneficiaryAddressCity(): Maybe<string> {
    if (this.beneficiaryAddress) {
      return this.beneficiaryAddress.townName;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get beneficiaryAddressCountry(): Maybe<string> {
    if (this.beneficiaryAddress) {
      return this.beneficiaryAddress.country;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get beneficiaryAddressStreet(): Maybe<string> {
    if (this.beneficiaryAddress) {
      return this.beneficiaryAddress.streetName;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get beneficiaryAddressNumber(): Maybe<string> {
    if (this.beneficiaryAddress) {
      return this.beneficiaryAddress.buildingNumber;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get beneficiaryAddressUnit(): Maybe<string> {
    if (this.beneficiaryAddress) {
      return this.beneficiaryAddress.buildingName;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get beneficiaryAddressState(): Maybe<string> {
    if (this.beneficiaryAddress) {
      return this.beneficiaryAddress.countrySubDivision;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get beneficiaryAddressPostCode(): Maybe<string> {
    if (this.beneficiaryAddress) {
      return this.beneficiaryAddress.postCode;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }

  get originatorFirstName(): Maybe<string> {
    if (this.originatorNatural) {
      const name = this.originatorNatural.name?.[0]?.nameIdentifier?.[0];
      return name ? name.secondaryIdentifier : undefined;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }

  get originatorLastName(): Maybe<string> {
    if (this.originatorLegal) {
      return this.originatorLegal.name?.nameIdentifier?.[0]?.legalPersonName;
    } else if (this.originatorNatural) {
      const name = this.originatorNatural.name?.[0]?.nameIdentifier?.[0];
      return name ? name.primaryIdentifier : undefined;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }

  get originatorName(): Maybe<string> {
    if (this.originatorLegal) {
      return this.originatorLegal.name?.nameIdentifier?.[0]?.legalPersonName;
    } else if (this.originatorNatural) {
      const name = this.originatorNatural.name?.[0]?.nameIdentifier?.[0];
      return name ? [name.secondaryIdentifier, name.primaryIdentifier].filter(Boolean).join(" ") : undefined;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get originatorDOB(): Maybe<string> {
    if (this.originatorNatural) {
      return this.originatorNatural.dateAndPlaceOfBirth?.dateOfBirth;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get originatorDOBCity(): Maybe<string> {
    if (this.originatorNatural) {
      return this.originatorNatural.dateAndPlaceOfBirth?.placeOfBirth;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get originatorAddress(): any {
    if (this.originatorLegal) {
      //To make Typescript happy, this line of code was needed
      return ((this.originatorLegal.geographicAddress as unknown) as unknown[])?.[0];
    } else if (this.originatorNatural) {
      return this.originatorNatural.geographicAddress?.[0];
    } else {
      return null;
    }
  }
  get originatorAddressLine1(): Maybe<string> {
    if (this.originatorAddress) {
      return this.originatorAddress.addressLine?.[0];
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get originatorAddressLine2(): Maybe<string> {
    if (this.originatorAddress) {
      return this.originatorAddress.addressLine?.[1];
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get originatorAddressCity(): Maybe<string> {
    if (this.originatorAddress) {
      return this.originatorAddress.townName;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get originatorAddressCountry(): Maybe<string> {
    if (this.originatorAddress) {
      return this.originatorAddress.country;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get originatorAddressStreet(): Maybe<string> {
    if (this.originatorAddress) {
      return this.originatorAddress.streetName;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get originatorAddressNumber(): Maybe<string> {
    if (this.originatorAddress) {
      return this.originatorAddress.buildingNumber;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get originatorAddressUnit(): Maybe<string> {
    if (this.originatorAddress) {
      return this.originatorAddress.buildingName;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get originatorAddressState(): Maybe<string> {
    if (this.originatorAddress) {
      return this.originatorAddress.countrySubDivision;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get originatorAddressPostCode(): Maybe<string> {
    if (this.originatorAddress) {
      return this.originatorAddress.postCode;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }

  get originatorIdentification(): Maybe<NationalIdentification> {
    if (this.originatorLegal) {
      return this.originatorLegal.nationalIdentification;
    } else if (this.originatorNatural) {
      return this.originatorNatural.nationalIdentification;
    } else {
      return null;
    }
  }
  get originatorIdentificationType(): Maybe<string> {
    if (this.originatorIdentification) {
      return this.originatorIdentification.nationalIdentifierType;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get originatorIdentificationNumber(): Maybe<string> {
    if (this.originatorIdentification) {
      return this.originatorIdentification.nationalIdentifier;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get originatorIdentificationAuthority(): Maybe<string> {
    if (this.originatorIdentification) {
      return this.originatorIdentification.registrationAuthority;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get originatorIdentificationCountry(): Maybe<string> {
    if (this.originatorIdentification) {
      return this.originatorIdentification.countryOfIssue;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get originatorDateOfRegistration(): Maybe<string> {
    if (this.originatorLegal) {
      return this.originatorLegal.dateOfRegistration;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }
  get originatorCountryOfRegistration(): Maybe<string> {
    if (this.originatorLegal) {
      return this.originatorLegal.countryOfRegistration;
    } else if (this.isEncrypted) {
      return ENCRYPTED_TEXT;
    } else {
      return null;
    }
  }

  get lastActionDate() {
    const { actions, createdAt } = this.originalTransaction;
    if (actions && actions?.length > 0) {
      //we just use the latest action date (they come date sorted from the BE)
      const lastAction = actions[actions.length - 1];
      return new Date(lastAction.createdAt);
    }
    return new Date(createdAt); //this should not happen, but just in case
  }

  get custom_asset_price() {
    return this.originalTransaction.custom_asset_price;
  }

  get comments() {
    return this.originalTransaction.comments || [];
  }
}

export const CATEGORIES = [
  {
    label: "Category: All",
    value: undefined,
  },
  {
    label: "Below Threshold",
    value: "BELOW_THRESHOLD",
  },
  {
    label: "Travel Rule",
    value: "TRAVELRULE",
  },
  {
    label: "Unhosted",
    value: "NON_CUSTODIAL",
  },
  {
    label: "Unknown",
    value: "UNKNOWN",
  },
];

export const SORT_OPTIONS = [
  {
    label: "Amount: Low to high",
    value: "transactionAmount:ASC",
  },
  {
    label: "Amount: High to low",
    value: "transactionAmount:DESC",
  },
  {
    label: "Time: Oldest to newest",
    value: "createdAt:ASC",
  },
  {
    label: "Time: Newest to oldest",
    value: "createdAt:DESC",
  },
];

export const UNDIRECTED_TRANSFERS_SORT_OPTIONS = [
  {
    label: "Date: Oldest to newest",
    value: "TIME_ASC",
  },
  {
    label: "Date: Newest to oldest",
    value: "TIME_DESC",
  },
];

export const DIRECTION = [
  {
    label: "Direction: All",
    value: undefined,
  },
  {
    label: "Incoming",
    value: "incoming",
  },
  {
    label: "Outgoing",
    value: "outgoing",
  },
  {
    label: "Internal",
    value: "internal",
  },
];

export const TRANSACTION_STATUSES = [
  { label: "Status: All", value: undefined },
  { label: "Sent", value: "SENT" },
  { label: "Pending", value: "PENDING" },
  { label: "New", value: "NEW" },
  { label: "Saved", value: "SAVED" },
  { label: "Canceled", value: "CANCELLED" },
  { label: "Received", value: "ACK" },
  { label: "Missing Data", value: "INCOMPLETE" },
  { label: "Missing Beneficiary Data", value: "MISSING_BENEFICIARY_DATA" },
  { label: "Accepted", value: "ACCEPTED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Declined", value: "DECLINED" },
  { label: "Pending contact information", value: "WAITING_FOR_INFORMATION" },
  { label: "Not Ready", value: "NOT_READY" },
  { label: "Missing data requested less than 5 days ago", value: "MISSING_DATA_REQUESTED_LT_5_DAYS" },
  { label: "Missing data requested more than 5 days ago", value: "MISSING_DATA_REQUESTED_GT_5_DAYS" },
  { label: "Originator not reached", value: "ORIGINATOR_UNREACHABLE" },
];

/**
 * Adapted from https://stackoverflow.com/a/27082406
 */
export function COUNT_DECIMALS(value: number | string) {
  value = Number(value);
  const text = value.toString();
  // verify if number 0.000005 is represented as "5e-6"
  if (text.indexOf("e-") > -1) {
    const [, trail] = text.split("e-");
    const deg = parseInt(trail, 10);
    return deg;
  }
  // count decimals for number in representation like "0.123456"
  if (Math.floor(value) !== value) {
    return text.split(".")[1].length || 0;
  }
  return 0;
}
